import {
  Button,
  Checkbox,
  CheckboxGroup,
  Flex,
  FormLabel,
  FormLabelProps,
  Grid,
  GridItem,
  HStack,
  Heading,
  Text,
  VStack,
} from '@chakra-ui/react'
import { useState } from 'react'
import { useForm } from 'react-hook-form'

import { showToast } from '../../components/Toast/showToast'
import { CookieCategory, UpdateUserSettingsInput } from '../../gql/graphql'
import { useAppStore } from '../../store/appStore/appStore'

export const UserSettingsEditor = () => {
  const { updateUserSettings, settings } = useAppStore(
    state => state.userSettings,
  )

  const { handleSubmit, setValue, watch, formState } =
    useForm<UpdateUserSettingsInput>({
      defaultValues: {
        cookieCategories: settings?.cookieCategories ?? [
          CookieCategory.Necessary,
        ],
      },
    })
  const [cookieAnalyticsIndeterminate, setCookieAnalyticsIndeterminate] =
    useState(!settings?.cookieCategories)

  const onSubmit = async (data: UpdateUserSettingsInput) => {
    setCookieAnalyticsIndeterminate(false)
    await updateUserSettings(data)
      .then(settings => {
        setValue('cookieCategories', settings?.cookieCategories ?? [])
        showToast({ title: 'Settings updated', variant: 'success' })
      })
      .catch(() => {
        showToast({ title: 'Failed to update settings.', variant: 'error' })
      })
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <VStack align="stretch" spacing={8} width="100%">
        <VStack align="stretch" spacing="8px">
          <Heading as="h2" fontSize="14px" fontWeight="500">
            Cookie Policy
          </Heading>
          <Text fontSize="14px" color="presets.neutral.60">
            We use cookies that are necessary to run our website. We use
            analytics cookies to understand website traffic and identify
            opportunities to enhance functionality and the user experience. We
            do not collect or store any personal information. You can manage
            your cookie preferences below.
          </Text>
          <CheckboxGroup
            value={watch('cookieCategories') as CookieCategory[]}
            onChange={values =>
              setValue('cookieCategories', values as CookieCategory[])
            }
          >
            <HStack pt="8px">
              <Grid
                templateColumns="repeat(2, 1fr)"
                rowGap="8px"
                columnGap="32px"
                pl="10px"
              >
                <GridItem>
                  <CookieCategoryLabel>Necessary Cookies</CookieCategoryLabel>
                </GridItem>
                <GridItem>
                  <Checkbox
                    value={CookieCategory.Necessary}
                    isDisabled={true}
                  />
                </GridItem>
                <GridItem>
                  <CookieCategoryLabel>Analytics Cookies</CookieCategoryLabel>
                </GridItem>
                <GridItem>
                  <Checkbox
                    value={CookieCategory.Analytics}
                    isIndeterminate={cookieAnalyticsIndeterminate}
                    onChange={e => setCookieAnalyticsIndeterminate(false)}
                  />
                </GridItem>
              </Grid>
            </HStack>
          </CheckboxGroup>
        </VStack>

        <Flex justify="flex-end">
          <Button
            type="submit"
            colorScheme="blue"
            isLoading={formState.isSubmitting}
          >
            Save Changes
          </Button>
        </Flex>
      </VStack>
    </form>
  )
}

const CookieCategoryLabel = ({ children, ...props }: FormLabelProps) => {
  return (
    <FormLabel
      as="label"
      fontSize="12px"
      fontWeight="500"
      color="presets.neutral.90"
      px="10px"
      py="4px"
      borderRadius="4px"
      bg="presets.neutral.10"
      {...props}
    >
      {children}
    </FormLabel>
  )
}
